import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, User, Phone, Home, Eye, EyeOff, Building2 } from 'lucide-react';

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const tabParam = searchParams.get('tab');
  const [isLogin, setIsLogin] = useState(tabParam !== 'register');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();
  
  const from = redirectParam || location.state?.from?.pathname || '/';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    isAgent: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Autentificare reușită!');
      } else {
        if (!formData.name.trim()) {
          toast.error('Introduceți numele');
          setLoading(false);
          return;
        }
        if (!formData.phone.trim()) {
          toast.error('Introduceți numărul de telefon');
          setLoading(false);
          return;
        }
        await register(formData.name, formData.email, formData.phone, formData.password, formData.isAgent);
        toast.success('Cont creat cu succes!');
      }
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || 'Eroare la autentificare');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo & Back */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-700 mb-4">
            <Home className="w-4 h-4" />
            Înapoi la anunțuri
          </Link>
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src="/imofi_logo.png" alt="Imofit" className="h-20 w-auto" />
          </div>
          <p className="text-stone-500">
            {isLogin ? 'Autentifică-te pentru a continua' : 'Creează un cont nou'}
          </p>
        </div>

        {/* Auth Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-stone-200">
          {/* Tabs */}
          <div className="flex mb-6 bg-stone-100 rounded-lg p-1">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                isLogin 
                  ? 'bg-white text-stone-900 shadow-sm' 
                  : 'text-stone-500 hover:text-stone-700'
              }`}
              data-testid="login-tab"
            >
              Autentificare
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                !isLogin 
                  ? 'bg-white text-stone-900 shadow-sm' 
                  : 'text-stone-500 hover:text-stone-700'
              }`}
              data-testid="register-tab"
            >
              Înregistrare
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name (only for register) */}
            {!isLogin && (
              <div>
                <Label htmlFor="name">Nume complet</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Ion Popescu"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="pl-10"
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="email@exemplu.ro"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="pl-10"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Phone (only for register) */}
            {!isLogin && (
              <div>
                <Label htmlFor="phone">Telefon</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+40 721 123 456"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="pl-10"
                    data-testid="phone-input"
                  />
                </div>
              </div>
            )}

            {/* Password */}
            <div>
              <Label htmlFor="password">Parolă</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="pl-10 pr-10"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {!isLogin && (
                <p className="text-xs text-stone-500 mt-1">Minim 6 caractere</p>
              )}
            </div>

            {/* Agent checkbox - only for register */}
            {!isLogin && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox 
                    checked={formData.isAgent}
                    onCheckedChange={(checked) => setFormData({...formData, isAgent: checked})}
                    className="mt-0.5"
                    data-testid="agent-checkbox"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-amber-600" />
                      <span className="font-medium text-stone-900">Sunt agent imobiliar</span>
                    </div>
                    <p className="text-xs text-stone-500 mt-1">
                      Agenții pot publica anunțuri nelimitate. Proprietarii au limită de 5 anunțuri.
                    </p>
                  </div>
                </label>
              </div>
            )}

            {/* Submit */}
            <Button 
              type="submit" 
              className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-white"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isLogin ? 'Se autentifică...' : 'Se creează contul...'}
                </>
              ) : (
                isLogin ? 'Autentificare' : 'Creează cont'
              )}
            </Button>
          </form>

          {/* Info */}
          <div className="mt-6 text-center text-sm text-stone-500">
            {isLogin ? (
              <p>
                Nu ai cont?{' '}
                <button 
                  onClick={() => setIsLogin(false)} 
                  className="text-amber-600 hover:text-amber-700 font-medium"
                >
                  Înregistrează-te
                </button>
              </p>
            ) : (
              <p>
                Ai deja cont?{' '}
                <button 
                  onClick={() => setIsLogin(true)} 
                  className="text-amber-600 hover:text-amber-700 font-medium"
                >
                  Autentifică-te
                </button>
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-stone-400 mt-6">
          Prin continuare, ești de acord cu Termenii și Condițiile noastre.
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
